// Settings → Tags & categories.

import { useState } from "react";
import { useAppData } from "../store";
import { Btn, Toggle } from "../components/Primitives";
import { Ic } from "../components/Icon";

export function TagsScreen() {
  const { data, saveConfig } = useAppData();
  const cats = data?.config.tagCategories ?? [];
  const [pick, setPick] = useState(cats[0]?.id ?? "");
  const cur = cats.find((c) => c.id === pick) ?? cats[0];
  const sectorTags = data?.config.sectorTags ?? [];

  if (!data || !cur) return null;

  const updateCategory = async (patch: Partial<typeof cur>) => {
    await saveConfig({
      ...data.config,
      tagCategories: data.config.tagCategories.map((c) =>
        c.id === cur.id ? { ...c, ...patch } : c,
      ),
    });
  };

  // Each category carries its own tag list; fall back to the legacy
  // sectorTags shape for the Sector category.
  const tagsForCat = cur.tags ?? (cur.id === "sector" ? sectorTags : []);

  const addTag = async () => {
    const label = window.prompt("New tag label?")?.trim();
    if (!label || tagsForCat.some((t) => t.label === label)) return;
    await updateCategory({
      tags: [...tagsForCat, { label, desc: "", color: "#5a564c", count: 0 }],
    });
  };

  const addCategory = async () => {
    const name = window.prompt("New category name?")?.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `cat-${Date.now()}`;
    if (data.config.tagCategories.some((c) => c.id === id)) return;
    await saveConfig({
      ...data.config,
      tagCategories: [
        ...data.config.tagCategories,
        { id, name, inBar: false, description: "", autoTag: "Manual only", tags: [] },
      ],
    });
    setPick(id);
  };

  return (
    <div className="page">
      <div className="topbar">
        <span className="crumb">Settings</span>
        <span className="crumb-sep">/</span>
        <h1>Tags & categories</h1>
        <span className="grow" />
        <Btn size="sm" ico="plus" kind="primary" onClick={addCategory}>New category</Btn>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", flex: 1, minHeight: 0 }}>
        <div style={{
          borderRight: "1px solid var(--border)",
          padding: "12px 10px", background: "var(--bg)", overflowY: "auto",
        }}>
          {cats.map((c) => (
            <div key={c.id} onClick={() => setPick(c.id)} style={{
              padding: "9px 10px", borderRadius: 5, cursor: "pointer",
              background: c.id === pick ? "var(--surface)" : "transparent",
              boxShadow: c.id === pick ? "0 0 0 1px var(--border)" : "none",
              display: "flex", alignItems: "center", gap: 8,
              fontSize: 13, marginBottom: 1,
              color: c.id === pick ? "var(--ink)" : "var(--ink-2)",
              fontWeight: c.id === pick ? 500 : 400,
            }}>
              <span style={{ color: "var(--ink-4)", cursor: "grab" }}>
                <Ic name="dots" size={12} />
              </span>
              <span style={{ flex: 1 }}>{c.name}</span>
              <span style={{
                fontSize: 9.5, color: c.inBar ? "var(--ink-3)" : "var(--ink-4)",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>{c.inBar ? "in bar" : "hidden"}</span>
            </div>
          ))}
        </div>
        <div className="scroll" style={{ padding: "22px 28px 60px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.015em" }}>
                {cur.name}
              </div>
              <div style={{
                color: "var(--ink-2)", fontSize: 12.5, marginTop: 4, maxWidth: 480,
              }}>{cur.description}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <Toggle on={cur.inBar} onChange={(v) => updateCategory({ inBar: v })} />
              <span style={{ fontSize: 12.5 }}>Show in Inbox tag bar</span>
            </div>
          </div>

          <div style={{
            marginTop: 18, padding: "10px 14px",
            border: "1px solid var(--border)", borderRadius: 6,
            background: "var(--bg)", fontSize: 12.5,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <Ic name="info" size={13} />
            <span>Auto-tag from field: <strong style={{ fontWeight: 500 }}>{cur.autoTag}</strong></span>
            <span style={{ color: "var(--ink-3)", marginLeft: "auto" }}>
              Values from this field become tags automatically.
            </span>
          </div>

          <div className="h-section" style={{ marginTop: 28 }}>
            <h3>Tags</h3>
            <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
              · {tagsForCat.length} in this category
            </span>
            <span className="grow" style={{ flex: 1 }} />
            <Btn size="sm" ico="plus" onClick={addTag}>Add tag</Btn>
          </div>
          {tagsForCat.length > 0 && (
            <div style={{
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 8, overflow: "hidden",
            }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "22px 18px 1.1fr 2fr 70px 32px",
                gap: 12, padding: "11px 16px",
                fontSize: 10.5, color: "var(--ink-3)",
                textTransform: "uppercase", letterSpacing: "0.08em",
                fontWeight: 500, borderBottom: "1px solid var(--border)",
                background: "var(--bg)",
              }}>
                <span /><span /><span>Tag</span><span>Description</span><span>Used by</span><span />
              </div>
              {tagsForCat.map((t, i) => (
                <div key={t.label} className="row" style={{
                  display: "grid",
                  gridTemplateColumns: "22px 18px 1.1fr 2fr 70px 32px",
                  gap: 12, padding: "12px 16px",
                  borderBottom: i < tagsForCat.length - 1 ? "1px solid var(--border)" : "none",
                  alignItems: "center", fontSize: 13,
                }}>
                  <span style={{ color: "var(--ink-4)", cursor: "grab" }}>
                    <Ic name="dots" size={14} />
                  </span>
                  <span style={{
                    width: 10, height: 10, borderRadius: 3,
                    background: t.color, display: "inline-block",
                  }} />
                  <span style={{ fontWeight: 500 }}>{t.label}</span>
                  <span style={{ color: "var(--ink-2)", fontSize: 12.5 }}>{t.desc}</span>
                  <span className="score" style={{ fontSize: 12, color: "var(--ink-3)" }}>{t.count}</span>
                  <span style={{ color: "var(--ink-3)", textAlign: "right" }}>
                    <Ic name="dots" size={14} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
